import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Card, CardContent, CardHeader } from '../../../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { MedicalCoverageSummaryCard } from './MedicalCoverageSummaryCard';
import coverageApiService from '../../../services/coverage-api.service';
import { mapMedicalCoverageResponse } from '../../../utils/coverageMapper';
import { sendToKinesis } from '../../../analytics/kinesisTransport';
import { useSelector } from 'react-redux';

/**
 * MedicalCoverageTab Component
 * Displays medical coverage information organized by indication
 */
export const MedicalCoverageTab = ({
  indications,
  productId,
  planId,
  drug,
  cachedData,
  onCacheUpdate,
}) => {
  const [expandedIndications, setExpandedIndications] = useState(new Set());
  
  // Get user info for analytics
  const userAttributes = useSelector((state) => state.auth?.userAttributes || {});

  const handleIndicationToggle = async (indicationId) => {
    const isExpanding = !expandedIndications.has(indicationId);
    
    // Toggle expansion state
    setExpandedIndications((prev) => {
      const newSet = new Set(prev);
      if (isExpanding) {
        newSet.add(indicationId);
      } else {
        newSet.delete(indicationId);
      }
      return newSet;
    });

    // If expanding and data not already fetched, fetch it
    if (isExpanding && !cachedData[indicationId]) {
      console.log('[Cache] Cache miss - fetching medical coverage for indication:', indicationId);
      // Set loading state
      onCacheUpdate(indicationId, { data: null, loading: true, error: null });

      try {
        const response = await coverageApiService.getMedicalCoverage(
          productId,
          planId,
          indicationId
        );

        if (!response || !response.result) {
          onCacheUpdate(indicationId, {
            data: null,
            loading: false,
            error: 'Failed to fetch medical coverage',
          });
        } else {
          const mappedData = mapMedicalCoverageResponse(response);

          if (!mappedData) {
            onCacheUpdate(indicationId, {
              data: null,
              loading: false,
              error: 'No medical coverage data available',
            });
          } else {
            onCacheUpdate(indicationId, {
              data: mappedData,
              loading: false,
              error: null,
            });
            
            // Track MMIT API call for medical coverage
            try {
              sendToKinesis('coverage_events', 'MMIT_API_CALLS_MADE', {
                userid: userAttributes?.sub || 'anonymous',
                organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
                practice_id: userAttributes?.['custom:organization_id'] || '',
                session_id: window.currentSessionId || '',
                feature_module: 'medical_coverage',
                mmit_api_call_count: 1,
                indicationId: indicationId?.toString() || '',
                planId: planId?.toString() || '',
                drugId: productId?.toString() || '',
                timestamp: new Date().toISOString()
              });
            } catch (analyticsError) {
              console.error('Analytics error (non-blocking):', analyticsError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching medical coverage:', error);
        onCacheUpdate(indicationId, {
          data: null,
          loading: false,
          error: 'An error occurred while fetching medical coverage',
        });
      }
    } else if (isExpanding && cachedData[indicationId]) {
      console.log('[Cache] Cache hit - using cached medical coverage for indication:', indicationId);
    }
  };

  if (!indications || indications.length === 0) {
    return (
      <div className="p-12 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto">
          <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4m0-4h.01" />
          </svg>
        </div>
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-foreground">
            Medical Benefit Information Not Provided
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            This plan has not provided medical benefit coverage information for this drug. Coverage may still exist through the pharmacy benefit.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">Medical Coverage by Indication</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Medical benefit coverage details for each approved indication
        </p>
      </div>

      {/* Indication Cards */}
      {indications.map((indication) => {
            const isExpanded = expandedIndications.has(indication.IndicationId);
            const cache = cachedData[indication.IndicationId];

            return (
              <Collapsible
                key={indication.IndicationId}
                open={isExpanded}
                onOpenChange={() => handleIndicationToggle(indication.IndicationId)}
              >
                <Card className="border-2 shadow-sm">
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-base font-medium text-foreground">{indication.Name}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {cache?.loading && (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          )}
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4 px-4">
                    {cache?.loading ? (
                      <div className="flex flex-col items-center justify-center py-16 px-4 bg-muted/30 rounded-lg border-2 border-dashed">
                        <Loader2 className="h-10 w-10 text-primary animate-spin mb-4" />
                        <p className="text-sm font-medium text-foreground mb-1">
                          Loading Medical Coverage for {indication.Name}...
                        </p>
                        <p className="text-xs font-normal text-muted-foreground text-center max-w-md">
                          Fetching medical benefit coverage details
                        </p>
                      </div>
                    ) : cache?.error ? (
                      <div className="flex flex-col items-center justify-center py-12 px-6 text-center space-y-4">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                          <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4m0-4h.01" />
                          </svg>
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-base font-semibold text-foreground">
                            Medical Coverage Data Not Provided
                          </h3>
                          <p className="text-sm text-muted-foreground max-w-md">
                            Medical benefit coverage information is not specified for this indication by the plan.
                          </p>
                        </div>
                      </div>
                    ) : cache?.data ? (
                      <MedicalCoverageSummaryCard medicalCoverageData={cache.data} drug={drug} />
                    ) : null}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
    </div>
  );
};
